import { useState } from 'react';
import styled from 'styled-components';
import {
  deleteThemeVersion,
  getThemeVersionConfig,
} from '../../../functions/API';
import { SessionData, useSessionStore } from '../../../hooks/stores/sessionStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { Dialog } from '../../common/Dialog';
import { Modal } from '../../common/Modal';
import { useScene } from '../../../context/sceneContext';

export const VersionHistoryModal = () => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [sessionData, setSessionData] = useSessionStore<SessionData>();

  const [IDsBeingRemoved, setIDsBeingRemoved] = useState<string[]>([]);

  const isOpen = UIData.openModalIDs.includes('versionHistoryModal');

  const handleDeleteVersion = async (versionID: string) => {
    if (!sessionData.themeID) return;

    setIDsBeingRemoved((prev) => [...prev, versionID]);

    await deleteThemeVersion({
      themeID: sessionData.themeID,
      versionID,
    });

    setSessionData({
      versions: sessionData.versions?.filter((item) => item.id !== versionID),
    });

    setIDsBeingRemoved((prev) => prev.filter((item) => item !== versionID));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => scene.closeModal('versionHistoryModal')}
      label="Version History"
    >
      {sessionData.versions ? (
        sessionData.versions.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '500px',
            }}
          >
            There are no saved versions yet
          </div>
        ) : (
          <div>
            {sessionData.versions
              ?.sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
              )
              ?.map((version) => {
                return (
                  <Item
                    key={version.id}
                    $disabled={IDsBeingRemoved.includes(version.id)}
                  >
                    <div>
                      <div className="buttons">
                        <div
                          onClick={() =>
                            scene.openModal('deleteVersionDialog-' + version.id)
                          }
                        >
                          <i className="icon-x" />
                        </div>
                        <div
                          onClick={async () => {
                            if (!sessionData.themeID) return;

                            const config = await getThemeVersionConfig({
                              themeID: sessionData.themeID,
                              versionID: version.id,
                            });
                            scene.importConfig(config);
                            scene.closeModal('versionHistoryModal');
                          }}
                        >
                          Import
                        </div>
                      </div>

                      <span className="versionCreateTime">
                        {convertToReadableDate(version.createdAt)}
                      </span>
                      <span className="versionID">{version.id}</span>
                    </div>
                    <img
                      loading="lazy"
                      src={version.thumbnailUrl || ''}
                      onError={() => {
                        version.thumbnailUrl = '';
                      }}
                    />

                    <Dialog
                      isOpen={UIData.openModalIDs.includes(
                        'deleteVersionDialog-' + version.id
                      )}
                      label="Delete Version"
                      onClose={() =>
                        scene.closeModal('deleteVersionDialog-' + version.id)
                      }
                      options={[
                        {
                          text: 'Cancel',
                          onClick: () =>
                            scene.closeModal(
                              'deleteVersionDialog-' + version.id
                            ),
                        },
                        {
                          text: 'Delete',
                          onClick: () => {
                            handleDeleteVersion(version.id);
                            scene.closeModal(
                              'deleteVersionDialog-' + version.id
                            );
                          },
                          type: 'danger',
                        },
                      ]}
                    >
                      Are you sure you want to delete this version?
                    </Dialog>
                  </Item>
                );
              })}
          </div>
        )
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
          }}
        >
          Loading...
        </div>
      )}
    </Modal>
  );
};

const convertToReadableDate = (timeString: string) => {
  const date = new Date(timeString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const Item = styled.div<{
  $disabled?: boolean;
}>`
  position: relative;
  user-select: none;
  box-sizing: border-box;
  height: 150px;
  margin: 5px 0;
  padding: 5px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border: 1px solid var(--neutral-3);
  border-radius: 10px;

  transition: background-color 0.3s ease;

  &:hover {
    background-color: var(--neutral-3);

    .buttons {
      opacity: 1;
      pointer-events: auto;
    }
  }

  & > img {
    cursor: pointer;
    max-height: 100%;
    max-width: 50%;
  }

  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .versionCreateTime {
      font-size: 13px;
      color: var(--main-1);
    }

    .versionID {
      user-select: text;
      font-size: 10px;
      color: var(--main-2);
    }
  }

  .buttons {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 10px;
    left: 10px;
    transition: opacity 0.3s;

    display: flex;
    gap: 5px;

    & > div {
      cursor: pointer;

      padding: 5px;
      border-radius: 5px;

      font-size: 12px;
      background-color: var(--neutral-2);
      transition: background-color 0.3s;

      &:hover {
        background-color: var(--neutral-1);
      }
    }
  }

  ${({ $disabled }) =>
    $disabled &&
    `
      pointer-events: none;
      opacity: 0.5;
  `}
`;
